import { Controller } from '@hotwired/stimulus'
import { createApp } from 'vue'
import consumer from '~/channels/consumer'

/**
 * Generation Lab Show Controller
 *
 * Stimulus controller for the Generation Lab run detail page.
 * Mounts the Vue app for displaying variants and block inspection.
 *
 * Data is passed inline from the server to avoid an extra JSON fetch,
 * making the initial render much faster.
 */
export default class extends Controller {
  static targets = ['loading']

  static values = {
    runId: String,
    initialData: Object,
    csrfToken: String
  }

  connect() {
    this.vueApp = null
    this.subscription = null

    // Mount Vue immediately with inline data (no fetch needed)
    this.mountVue()
    this.subscribeToRun()
  }

  disconnect() {
    this.unmountVue()
    this.unsubscribe()
  }

  async mountVue() {
    if (this.vueApp) return

    // Dynamic import of the Vue component
    const { default: GenerationLabShow } = await import('~/javascript/components/admin/generation_lab/GenerationLabShow.vue')

    // Hide skeleton loading
    if (this.hasLoadingTarget) {
      this.loadingTarget.style.display = 'none'
    }

    const props = {
      runId: this.runIdValue,
      initialData: this.initialDataValue || {},
      csrfToken: this.csrfTokenValue || document.querySelector('meta[name="csrf-token"]')?.content,
      onVariantUpdated: (variant) => this.handleVariantUpdated(variant)
    }

    this.vueApp = createApp(GenerationLabShow, props)
    this.vueApp.mount(this.element)
  }

  unmountVue() {
    if (this.vueApp) {
      this.vueApp.unmount()
      this.vueApp = null
    }
  }

  subscribeToRun() {
    if (!this.runIdValue) return

    this.subscription = consumer.subscriptions.create(
      { channel: 'GenerationLabChannel', run_id: this.runIdValue },
      {
        connected: () => {
          console.log('[GenerationLabShow] Connected to run channel')
        },
        received: (data) => {
          this.handleChannelMessage(data)
        }
      }
    )
  }

  unsubscribe() {
    if (this.subscription) {
      this.subscription.unsubscribe()
      this.subscription = null
    }
  }

  handleChannelMessage(data) {
    // Dispatch a custom event that the Vue component can listen to
    this.element.dispatchEvent(new CustomEvent('generation-lab:channel-message', {
      detail: data,
      bubbles: true
    }))

    // Auto-refresh on completion
    if (data.event === 'run_completed') {
      // Give Vue a moment to update, then refresh to get latest data
      setTimeout(() => {
        window.location.reload()
      }, 1000)
    }
  }

  handleVariantUpdated(variant) {
    // Update local data for persistence across real-time updates
    if (this.initialDataValue && this.initialDataValue.variants) {
      const idx = this.initialDataValue.variants.findIndex((v) => v.id === variant.id)
      if (idx !== -1) {
        this.initialDataValue.variants[idx] = variant
      }
    }
  }
}
